import React, { createContext, useState, useContext, useEffect } from "react";

// Create the context
const CartContext = createContext();

// Create a custom hook to use the context easily
export const useCart = () => {
  return useContext(CartContext);
};

// Create the Provider component
export const CartProvider = ({ children }) => {
  // --- 1. LOAD CART FROM STORAGE ---
  const [cartItems, setCartItems] = useState(() => {
    const savedCart = localStorage.getItem("dukamoCart");
    return savedCart ? JSON.parse(savedCart) : [];
  });

  // --- 2. LOAD DELIVERY OPTION FROM STORAGE ---
  const [deliveryOption, setDeliveryOption] = useState(() => {
    const savedOption = localStorage.getItem("dukamoDeliveryOption");
    return savedOption ? JSON.parse(savedOption) : null;
  });

  // --- LOCATION STATE ---
  const [userLocation, setUserLocation] = useState("ET");
  const [currency, setCurrency] = useState("ETB");

  // --- NOTIFICATION STATE ---
  const [notification, setNotification] = useState(null);

  const notify = (message, type = "error") => {
    setNotification({ message, type });
  };

  const clearNotification = () => {
    setNotification(null);
  };

  // Detect Location (Run once)
  useEffect(() => {
    const fetchLocation = async () => {
      try {
        const res = await fetch("https://ipapi.co/json/");
        const data = await res.json();
        if (["US", "GB", "CA"].includes(data.country_code)) {
          setUserLocation(data.country_code);
          setCurrency("USD");
        } else {
          setUserLocation("ET");
          setCurrency("ETB");
        }
      } catch (error) {
        // Default to Ethiopia if api fails
      }
    };
    fetchLocation();
  }, []);

  // --- SAVE CART TO STORAGE ---
  useEffect(() => {
    localStorage.setItem("dukamoCart", JSON.stringify(cartItems));
  }, [cartItems]);

  // --- SAVE DELIVERY OPTION TO STORAGE ---
  useEffect(() => {
    if (deliveryOption) {
      localStorage.setItem(
        "dukamoDeliveryOption",
        JSON.stringify(deliveryOption)
      );
    }
  }, [deliveryOption]);

  // --- ADD TO CART (Silent Success, Loud Limit Error) ---
  const addToCart = (product) => {
    setCartItems((prevItems) => {
      const existingItem = prevItems.find((item) => item.id === product.id);

      // Default limits if missing from DB
      const min = product.minQuantity || 1;
      const max = product.maxQuantity || 10;

      if (existingItem) {
        const newQuantity = existingItem.quantity + 1;

        // Check Max Limit
        // We notify here because the user is on the Product Page
        // and needs to know why the 'Buy' button stopped working.
        if (newQuantity > max) {
          notify(
            `Limit Reached! Maximum allowed is ${max} for this item.`,
            "error"
          );
          return prevItems; // Do not update
        }

        // Success: Update quantity (SILENT - No Popup)
        return prevItems.map((item) =>
          item.id === product.id ? { ...item, quantity: newQuantity } : item
        );
      } else {
        // New Item: Start at Minimum Quantity (SILENT - No Popup)
        return [
          ...prevItems,
          {
            ...product,
            quantity: min,
            minQuantity: min,
            maxQuantity: max,
          },
        ];
      }
    });
  };

  const removeFromCart = (productId) => {
    setCartItems((prevItems) =>
      prevItems.filter((item) => item.id !== productId)
    );
    // Silent removal
  };

  // --- UPDATE QUANTITY (Silent Failsafe) ---
  const updateQuantity = (productId, newQuantity) => {
    setCartItems((prevItems) => {
      const item = prevItems.find((i) => i.id === productId);
      if (!item) return prevItems;

      // 1. Handle Removal
      if (newQuantity <= 0) {
        return prevItems.filter((item) => item.id !== productId);
      }

      // 2. Check Min Limit
      // We don't notify because the UI button is already disabled.
      // This just prevents code manipulation.
      if (newQuantity < item.minQuantity) {
        return prevItems;
      }

      // 3. Check Max Limit
      // We don't notify because the UI button is already disabled.
      if (newQuantity > item.maxQuantity) {
        return prevItems;
      }

      // 4. Update if valid
      return prevItems.map((item) =>
        item.id === productId ? { ...item, quantity: newQuantity } : item
      );
    });
  };

  const clearCart = () => {
    setCartItems([]);
    setDeliveryOption(null);
    localStorage.removeItem("dukamoDeliveryOption");
  };

  const getPrice = (etbPrice) => {
    if (currency === "USD") {
      return (etbPrice / 120).toFixed(2);
    }
    return etbPrice;
  };

  const value = {
    cartItems,
    addToCart,
    removeFromCart,
    updateQuantity,
    clearCart,
    deliveryOption,
    setDeliveryOption,
    userLocation,
    currency,
    getPrice,
    // Export notification state
    notification,
    clearNotification,
  };

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
};
